import api from "./api"
import type { FieldGroup, FieldGroupTree } from "./field-library"
import type { AuditSetFields } from "./audit-sets"
import type { CanvasLayout, SectionNode } from "./content-types"
import type { CollabKaynak } from "./content-type-collab"
import type { MusteriKunyeKaynagi } from "./company-templates"

export type ProjectStatus = "DRAFT" | "ACTIVE" | "COMPLETED" | "CANCELLED"

export interface ElevatorProjectSummary {
  id: string
  firmaUnvani: string
  status: ProjectStatus
  cinsi: string
  tahrikTipi: string
  faaliyetTuru: string
  seriNo: string | null
  asansorAdresi: string | null
  createdAt: string
  // Faaliyet Dosyaları (Faz A): üst-bilgi alanları
  dosyaNo: string | null
  modul: string | null
  musteriId: string | null
  musteriUnvani: string | null
  sorumluPersonel: string | null
  /** Kapsam / alan. */
  scopeId?: string | null
  /** Dosyanın denetim seti (bir dosyanın tek seti olur). */
  denetimSeti?: { id: string; name: string; code: string | null } | null
  /** İstenen alanların değerleri (`fields` parametresi) — alan kolonları. */
  alanlar?: Record<string, unknown>
}

export interface PaginatedElevatorProjects {
  data: ElevatorProjectSummary[]
  total: number
  totalPages: number
  page: number
  pageSize: number
}

export interface ElevatorProject {
  id: string
  status: ProjectStatus
  // Applicant
  firmaUnvani: string
  adres: string
  telefon: string
  eposta: string
  yetkiliAdSoyad: string
  yetkiliGorevi: string
  // Elevator
  tahrikTipi: string
  cinsi: string
  kapasite: number
  hiz: number
  durakSayisi: number
  seriNo: string | null
  asansorAdresi: string | null
  // Interpreter
  tercumanGerekli: boolean
  dil: string | null
  // Assessment
  uygunlukDegerlendirmeYontemi: string
  ilgiliStandart: string
  faaliyetTuru: string
  // Addresses
  tamamlanmisMontajAdresleri: string[] | null
  yariMontajAdresleri: string[] | null
  // Module H1/E
  cokluTesis: boolean | null
  vardiyaSistemi: boolean | null
  danismanlikHizmeti: boolean | null
  calisanSayisiNormal: number | null
  calisanSayisiTaseron: number | null
  disKaynakliProses: string | null
  haricTutulanMaddeler: string | null
  // Deviations
  kuyuDibiYukseklik: number | null
  kuyuUstuYukseklik: number | null
  calismaAlaniYukseklik: number | null
  // Signature
  basvuruYetkilisiAdSoyad: string | null
  kaseImza: boolean | null
  tarih: string | null
  // Dynamic content type (Content Type Engine)
  data?: Record<string, unknown> | null
  contentTypeVersionId?: string | null
  // Faaliyet Dosyaları (Faz A): üst-bilgi alanları + ilişkiler (findOne)
  dosyaNo?: string | null
  modul?: string | null
  customerId?: string | null
  /** Detayda müşterinin tüm bilgileri + ana yetkili (künye için); listede yalnız ad. */
  customer?: ({ id: string; firmaUnvani: string; kisaAd: string | null } & MusteriKunyeKaynagi) | null
  scopeId?: string | null
  scope?: { id: string; title: string; code: string | null; parentId: string | null } | null
  sorumluPersonelId?: string | null
  sorumluPersonel?: { id: string; firstName: string; lastName: string } | null
  companyId?: string | null
  // Timestamps
  createdAt: string
  updatedAt: string
  activatedAt: string | null
  completedAt: string | null
  cancelledAt: string | null
  // Relations (from findOne)
  inspectionSets?: InspectionSetAssignment[]
  statusHistory?: StatusHistoryEntry[]
}

export interface InspectionSetAssignment {
  id: string
  auditSetId: string
  auditSet: {
    id: string
    name: string
    code?: string | null
    _count: {
      checklists: number
    }
    /** ESKİ doğrudan bağ. Yeni setlerde boş kalır — tek başına güvenilmez. */
    checklists: {
      id: string
      checklistId: string
      checklist: {
        id: string
        code: string
        description: string | null
        deletedAt: string | null
      }
    }[]
    /**
     * GÜNCEL bağ: menü ağacına yerleştirilen kontrol listesi öğeleri.
     * Denetim seti oluşturucusu artık buraya yazıyor.
     */
    items: {
      id: string
      checklistId: string | null
      checklist: {
        id: string
        code: string
        description: string | null
        deletedAt: string | null
      } | null
    }[]
  }
  createdAt: string
}

export interface StatusHistoryEntry {
  id: string
  previousStatus: ProjectStatus
  newStatus: ProjectStatus
  changedByUserId: string
  changedAt: string
}

export interface ElevatorProjectQueryParams {
  page?: number
  pageSize?: number
  search?: string
  status?: string // comma-separated
  faaliyetTuru?: string
  /** Kapsam / alan; "none" = kapsamsız. */
  scopeId?: string
  /** Kolon olarak gösterilecek alan anahtarları (virgülle). */
  fields?: string
  /** Sıralama kolonu (ör. "dosyaNo", "alan:kapasite") ve yönü. */
  sortBy?: string
  sortDir?: "asc" | "desc"
}

// Data-öncelikli (Faz 6): tüm form alanları yayınlanmış şemaya göre `data`
// içinde (snake_case key'li) gönderilir.
export interface CreateElevatorProjectPayload {
  data: Record<string, unknown>
  // Faaliyet Dosyaları (Faz A): opsiyonel ilişki/üst-bilgi alanları
  customerId?: string
  modul?: string
  sorumluPersonelId?: string
}

// Güncellemede data opsiyonel; yalnız üst-bilgi alanları da güncellenebilir.
// customerId/sorumluPersonelId "" → bağı kaldırır.
export interface UpdateElevatorProjectPayload {
  data?: Record<string, unknown>
  /** Künye: form doğrulanmadan yalnız dosya numarası / ünvan değişir. */
  dosyaNo?: string
  firmaUnvani?: string
  customerId?: string
  modul?: string
  sorumluPersonelId?: string
}

export interface ManageAddressesPayload {
  tamamlanmisMontajAdresleri?: string[]
  yariMontajAdresleri?: string[]
}

export interface ManageModuleH1EPayload {
  cokluTesis?: boolean
  vardiyaSistemi?: boolean
  danismanlikHizmeti?: boolean
  calisanSayisiNormal?: number
  calisanSayisiTaseron?: number
  disKaynakliProses?: string
  haricTutulanMaddeler?: string
}

export interface ManageReviewersPayload {
  userIds: string[]
}

export interface UpdateStatusPayload {
  status: ProjectStatus
}

export async function fetchElevatorProjects(params: ElevatorProjectQueryParams = {}) {
  const { data } = await api.get<PaginatedElevatorProjects>("/elevator-projects", { params })
  return data
}

export async function fetchElevatorProject(id: string) {
  const { data } = await api.get<ElevatorProject>(`/elevator-projects/${id}`)
  return data
}

export async function createElevatorProject(payload: CreateElevatorProjectPayload) {
  const { data } = await api.post<ElevatorProject>("/elevator-projects", payload)
  return data
}

/** Hızlı oluşturma: kapsam + denetim seti + müşteri (başvuru formu sonra). */
export async function quickCreateElevatorProject(payload: {
  scopeId: string
  auditSetId: string
  customerId: string
}) {
  const { data } = await api.post<ElevatorProject>("/elevator-projects/quick", payload)
  return data
}

/**
 * Bir kapsam/alanın aktif VARSAYILAN alan grubu (ağaç) — liste kolonları.
 * Alan verilirse üst kapsamınki; yoksa null.
 */
export async function fetchScopeDefaultFields(scopeId: string) {
  const { data } = await api.get<FieldGroupTree | null>("/elevator-projects/scope-default-fields", {
    params: { scopeId },
  })
  return data || null
}

/**
 * Seçili kapsam/alana atanmış ÖTEKİ alan grupları (alanlarıyla) — listede
 * isteğe bağlı gösterilen kolonlar. Aktif varsayılan grup burada yok.
 */
export async function fetchScopeFieldGroups(scopeId: string) {
  const { data } = await api.get<FieldGroup[]>("/elevator-projects/scope-fields", { params: { scopeId } })
  return data
}

/** Sol sütun: kapsam başına dosya sayısı (`scopeId: null` = kapsamsız). */
export async function fetchElevatorProjectScopeCounts() {
  const { data } = await api.get<{ scopeId: string | null; count: number }[]>("/elevator-projects/scope-counts")
  return data
}

/** Firmanın faaliyet dosyası numarası deseni (ör. "{KAPSAM}-{YIL}-{SIRA:5}"). */
export async function fetchDosyaNoDeseni(companyId?: string) {
  const { data } = await api.get<{ dosyaNoDeseni: string }>("/elevator-projects/settings/dosya-no", { params: { companyId } })
  return data
}

export async function saveDosyaNoDeseni(dosyaNoDeseni: string, companyId?: string) {
  const { data } = await api.put<{ dosyaNoDeseni: string }>("/elevator-projects/settings/dosya-no", { dosyaNoDeseni, companyId })
  return data
}

/** Desenin örnek çıktısı (önizleme): sunucudaki üreticinin aynı jetonları. */
export function dosyaNoOrnegi(desen: string, kapsamKodu = "ASN", sira = 125): string {
  const t = new Date()
  const yil = String(t.getFullYear())
  return desen
    .replace(/\{YIL\}/g, yil)
    .replace(/\{YY\}/g, yil.slice(2))
    .replace(/\{AY\}/g, String(t.getMonth() + 1).padStart(2, "0"))
    .replace(/\{KAPSAM\}/g, kapsamKodu)
    .replace(/\{SIRA(?::(\d))?\}/g, (_, n) => String(sira).padStart(n ? Number(n) : 1, "0"))
}

/** Genel Bilgiler formunun iki kaynağı. */
export interface GeneralFormSources {
  /** Dosyanın kapsamının aktif varsayılan alan grubu (ağaç); yoksa null. */
  varsayilan: FieldGroupTree | null
  /** Dosyanın denetim setindeki formların alanları (tekil) ve gruplaması. */
  setAlanlari: AuditSetFields | null
}

/**
 * GENEL BİLGİLER FORMU — kapsamın varsayılan grubu + denetim setinin alanları.
 * İkisi de boşsa ekran eski başvuru formuna düşer.
 */
export async function fetchGeneralForm(projectId: string) {
  const { data } = await api.get<GeneralFormSources>(`/elevator-projects/${projectId}/general-form`)
  return data
}

/**
 * Dosya DEĞERLERİ — Genel Bilgiler ve Doldur sayfasındaki alanlar dosyanın
 * `data`sına birleştirilir (tek kaynak); `null` anahtarı siler.
 */
/** "Personel" alanından açılan / iptal edilen görevlerin özeti (sunucu döner). */
export interface FieldTaskAssignment {
  field: string
  assigned?: string
  issues?: { code: string; severity: string; message: string }[]
  cancelled: number
  skipped?: string
}

export async function updateProjectValues(id: string, values: Record<string, unknown>) {
  const { data } = await api.patch<ElevatorProject & { taskAssignments?: FieldTaskAssignment[] }>(`/elevator-projects/${id}/values`, { values })
  return data
}

/**
 * DOSYA FORMU — denetim setindeki bir formun bu dosyadaki örneği. Satırı
 * olmayan form asıldır (kopya 1) ve Genel Bilgiler'den doldurulur.
 */
export interface DosyaFormu {
  id: string
  auditSetItemId: string
  /** 1 = asıl; çoğaltılan kopyalar 2, 3… */
  kopyaNo: number
  /** Örneğin dosyadaki adı (kopyalar elle adlandırılır); null = formun adı. */
  baslik?: string | null
  /** true: Genel Bilgiler'de sorulan alanlar oradan gelir (formda kilitli). */
  genelBilgilerden: boolean
  /** Formun kendi değerleri, formun şeklinde. */
  data: Record<string, unknown>
  /**
   * "Doldurmaya başla"da dondurulan şekil. null = başlanmadı: form en güncel
   * hâliyle (salt-okunur) gösterilir.
   */
  sema: DosyaFormuSemasi | null
  baslangic: string | null
  /** "Tamamla"ya basıldığı an; dolu = örnek salt-okunur (yeniden açılana dek). */
  tamamlanma?: string | null
  tamamlayan?: string | null
  /** BEKLIYOR: tamamlandı, onay bekliyor · ONAYLANDI · REDDEDILDI: doldurana iade. */
  onayDurumu?: FormOnayDurumu | null
  onaylayan?: string | null
  onayTarihi?: string | null
  /** Onaylayanın notu ya da iade gerekçesi. */
  onayNotu?: string | null
  /** Bitmiş hâlin müşteriyle son paylaşıldığı an. */
  paylasimTarihi?: string | null
  updatedAt: string
}

export type FormOnayDurumu = "BEKLIYOR" | "ONAYLANDI" | "REDDEDILDI"

export interface DosyaFormuSemasi {
  contentTypeId: string
  revisionNo: string | null
  layoutJson: CanvasLayout | null
  sections: SectionNode[]
}

/** Örneği başlatır: formun o anki hâli dondurulur. */
export async function dosyaFormunuBaslat(projectId: string, auditSetItemId: string, kopyaNo: number) {
  const { data } = await api.post<DosyaFormu>(`/elevator-projects/${projectId}/forms/start`, { auditSetItemId, kopyaNo })
  return data
}

/** "Tamamla": örnek kilitlenir ve onaya düşer. */
export async function dosyaFormunuTamamla(projectId: string, auditSetItemId: string, kopyaNo: number) {
  const { data } = await api.post<DosyaFormu>(`/elevator-projects/${projectId}/forms/complete`, { auditSetItemId, kopyaNo })
  return data
}

/** Tamamlanmış örneği yeniden düzenlemeye açar (yönetici). */
export async function dosyaFormunuYenidenAc(projectId: string, auditSetItemId: string, kopyaNo: number) {
  const { data } = await api.post<DosyaFormu>(`/elevator-projects/${projectId}/forms/reopen`, { auditSetItemId, kopyaNo })
  return data
}

/** Onay ya da gerekçeyle iade. */
export async function dosyaFormunuOnayla(projectId: string, auditSetItemId: string, kopyaNo: number, onay: boolean, not?: string) {
  const { data } = await api.post<DosyaFormu>(`/elevator-projects/${projectId}/forms/approve`, { auditSetItemId, kopyaNo, onay, not })
  return data
}

/** Paylaşım kanalı: e-posta sunucudan gider; WhatsApp ve bağlantıda gönderimi kullanıcı yapar. */
export type PaylasimKanali = "EPOSTA" | "WHATSAPP" | "BAGLANTI"

/** Bir gönderim — gönderim geçmişinin satırı. `url` oturumsuz açılan public bağlantı. */
export interface DosyaPaylasimi {
  id: string
  hedef: string | null
  kopyaNo: number | null
  baslik: string
  kanal: PaylasimKanali
  alici: string | null
  mesaj: string | null
  gonderen: string | null
  expiresAt: string | null
  acilmaSayisi: number
  sonAcilma: string | null
  createdAt: string
  url: string
}

export const paylasimlarAnahtari = (projectId: string) => ["elevator-project-shares", projectId] as const

/** Bitmiş formu paylaşır: PDF sunucuda saklanır, public bağlantı açılır (e-postada gönderilir). */
export async function dosyaFormunuPaylas(
  projectId: string,
  body: { auditSetItemId: string; kopyaNo: number; kanal: PaylasimKanali; alici?: string; mesaj?: string; data: Record<string, unknown> },
) {
  const { data } = await api.post<DosyaPaylasimi>(`/elevator-projects/${projectId}/forms/share`, body)
  return data
}

export async function fetchPaylasimlar(projectId: string, filtre?: { hedef?: string; kopyaNo?: number }) {
  const { data } = await api.get<DosyaPaylasimi[]>(`/elevator-projects/${projectId}/shares`, { params: filtre })
  return data
}

/** FORMUN HARİCİ DOKÜMANLARI — PDF ve görseller, sırasıyla. */
export interface HariciDokuman {
  id: string
  sortOrder: number
  /** PDF/Yazdır bölümüne gönderildi. */
  ciktida?: boolean
  /** Bağlantı yokken eklendi; cihazda bekliyor (henüz sunucuda yok). */
  cevrimdisi?: boolean
  createdAt: string
  file: { id: string; filename: string; mimeType: string; size: number }
}

export const hariciDokumanAnahtari = (projectId: string, auditSetItemId: string, kopyaNo: number) =>
  ["elevator-project-form-files", projectId, auditSetItemId, kopyaNo] as const

export async function fetchHariciDokumanlar(projectId: string, auditSetItemId: string, kopyaNo: number) {
  const { data } = await api.get<HariciDokuman[]>(`/elevator-projects/${projectId}/forms/files`, { params: { auditSetItemId, kopyaNo } })
  return data
}

export async function hariciDokumanEkle(projectId: string, auditSetItemId: string, kopyaNo: number, fileId: string) {
  await api.post(`/elevator-projects/${projectId}/forms/files`, { auditSetItemId, kopyaNo, fileId })
}

export async function hariciDokumanlariSirala(projectId: string, ids: string[]) {
  await api.patch(`/elevator-projects/${projectId}/forms/files/reorder`, { ids })
}

export async function hariciDokumanSil(projectId: string, id: string) {
  await api.delete(`/elevator-projects/${projectId}/forms/files/${id}`)
}

export async function hariciDokumanAdresi(projectId: string, id: string) {
  const { data } = await api.get<{ url: string }>(`/elevator-projects/${projectId}/forms/files/${id}/url`)
  return data.url
}

/** Örneğin PDF'i (dondurulmuş şekliyle, verilen değerlerle) — dosya adıyla birlikte. */
export async function dosyaFormuPdf(projectId: string, auditSetItemId: string, kopyaNo: number, degerler: Record<string, unknown>) {
  const res = await api.post<Blob>(
    `/elevator-projects/${projectId}/forms/pdf`,
    { auditSetItemId, kopyaNo, data: degerler },
    { responseType: "blob" },
  )
  const disposition = String(res.headers["content-disposition"] ?? "")
  const eslesme = /filename\*=UTF-8''([^;]+)/i.exec(disposition)
  // Başlık CORS'ta açık değilse okunamaz: o zaman ad çağıranın verdiği.
  return { blob: res.data, dosyaAdi: eslesme ? decodeURIComponent(eslesme[1]) : null }
}

/**
 * DOSYANIN İŞLEM GEÇMİŞİ — durum, set, form süreci, kontrol listeleri,
 * görevlendirmeler, ekler. `hedef` verilirse yalnız o öğenin (formun) olayları.
 */
export interface DosyaOlayi {
  id: string
  tur: string
  ozet: string
  hedef: string | null
  kopyaNo: number | null
  veri: Record<string, unknown> | null
  kullanici: string
  createdAt: string
}

export const dosyaOlaylariAnahtari = (projectId: string) => ["elevator-project-events", projectId] as const

export async function fetchDosyaOlaylari(projectId: string, filtre?: { hedef?: string; kopyaNo?: number }) {
  const { data } = await api.get<DosyaOlayi[]>(`/elevator-projects/${projectId}/events`, { params: filtre })
  return data
}

/** PDF/Yazdır'dan paylaşılan belgeyi geçmişe yazar. */
export async function belgePaylasildi(projectId: string, baslik: string, hedef?: string) {
  await api.post(`/elevator-projects/${projectId}/events/shared`, { baslik, hedef })
}

export async function fetchDosyaFormlari(projectId: string) {
  const { data } = await api.get<DosyaFormu[]>(`/elevator-projects/${projectId}/forms`)
  return data
}

export async function dosyaFormunuYaz(
  projectId: string,
  body: { auditSetItemId: string; kopyaNo: number; genelBilgilerden?: boolean; data?: Record<string, unknown>; baslik?: string | null },
) {
  const { data } = await api.put<DosyaFormu>(`/elevator-projects/${projectId}/forms`, body)
  return data
}

export async function dosyaFormunuCogalt(projectId: string, auditSetItemId: string, kaynakKopyaNo: number) {
  const { data } = await api.post<DosyaFormu>(`/elevator-projects/${projectId}/forms/duplicate`, { auditSetItemId, kaynakKopyaNo })
  return data
}

export async function dosyaFormunuSil(projectId: string, formId: string) {
  await api.delete(`/elevator-projects/${projectId}/forms/${formId}`)
}

export async function updateElevatorProject(id: string, payload: UpdateElevatorProjectPayload) {
  const { data } = await api.patch<ElevatorProject>(`/elevator-projects/${id}`, payload)
  return data
}

export async function updateElevatorProjectAddresses(id: string, payload: ManageAddressesPayload) {
  const { data } = await api.patch<ElevatorProject>(`/elevator-projects/${id}/addresses`, payload)
  return data
}

export async function updateElevatorProjectModuleH1E(id: string, payload: ManageModuleH1EPayload) {
  const { data } = await api.patch<ElevatorProject>(`/elevator-projects/${id}/module-h1e`, payload)
  return data
}

export async function updateElevatorProjectStatus(id: string, payload: UpdateStatusPayload) {
  const { data } = await api.patch<ElevatorProject>(`/elevator-projects/${id}/status`, payload)
  return data
}

/** Dosyanın (tek) denetim setini değiştirir; set yoksa atar. */
export async function replaceProjectInspectionSet(projectId: string, auditSetId: string) {
  const { data } = await api.put(`/elevator-projects/${projectId}/inspection-set`, { auditSetId })
  return data
}

export async function configureProjectReviewers(projectId: string, payload: ManageReviewersPayload) {
  const { data } = await api.put(`/elevator-projects/${projectId}/reviewers`, payload)
  return data
}

// === Sağ panel (Notlar) ve bölüm ekleri ===

/**
 * Notlar sekmesinin bağlandığı kaynak — form/antet notlarıyla aynı uçlar
 * (`notes`, `notes/reorder`, `notes/:id`), yalnız kök adres farklı.
 */
export const dosyaKaynagi = (projectId: string): CollabKaynak => ({
  kok: `/elevator-projects/${projectId}`,
  anahtar: ["elevator-project", projectId],
  cevrimdisiProjeId: projectId,
})

export type DosyaOgeTuru = "DOSYA" | "FORM" | "DOKUMAN" | "KONTROL_LISTESI"

/**
 * Dosyaya özel öğe — denetim setinin bir bölümüne (ya da dosyaya özel bir alt
 * menüye) YALNIZ bu dosyada eklenen harici belge, sistem formu ya da doküman.
 */
export interface DosyaEki {
  id: string
  elevatorProjectId: string
  tur: DosyaOgeTuru
  /** Setin bölümü; `projeMenuId` ile ikisi de null = kök (menüsüz). */
  auditSetMenuId: string | null
  projeMenuId: string | null
  fileId: string | null
  contentTypeKey: string | null
  documentId: string | null
  title: string | null
  sortOrder: number
  /** PDF/Yazdır bölümüne gönderildi (yalnız DOSYA). */
  ciktida?: boolean
  createdAt: string
  file: { id: string; filename: string; mimeType: string; size: number } | null
  document: { id: string; title: string; fileId: string | null } | null
  /** FORM: formun güncel revizyonu. */
  form: { key: string; label: string; code: string | null; revisionNo: string | null } | null
  checklistId?: string | null
  /** KONTROL_LISTESI: listenin güncel revizyonu. */
  kontrolListesi?: { id: string; title: string; code: string; revisionNo: string | null } | null
}

/** Dosyaya özel alt menü — setin bir bölümünün ya da başka bir dosya menüsünün altında. */
export interface DosyaMenusu {
  id: string
  elevatorProjectId: string
  auditSetMenuId: string | null
  parentId: string | null
  label: string
  sortOrder: number
}

/** Öğenin/menünün ağaçtaki yeri. */
export interface DosyaYeri {
  auditSetMenuId?: string | null
  projeMenuId?: string | null
}

/** Yüklenebilen türler — sunucu da aynı listeyi doğruluyor. */
export const DOSYA_EKI_ACCEPT = ".pdf,.xls,.xlsx,.doc,.docx"

export function dosyaEkiIzinliMi(filename: string) {
  return /\.(pdf|xlsx?|docx?)$/i.test(filename)
}

export async function fetchDosyaEkleri(projectId: string) {
  const { data } = await api.get<{ menuler: DosyaMenusu[]; ogeler: DosyaEki[] }>(`/elevator-projects/${projectId}/attachments`)
  return data
}

export async function dosyaEkiEkle(
  projectId: string,
  body: DosyaYeri & { tur?: DosyaOgeTuru; fileId?: string; contentTypeKey?: string; documentId?: string; checklistId?: string; title?: string },
) {
  const { data } = await api.post<DosyaEki>(`/elevator-projects/${projectId}/attachments`, body)
  return data
}

/** Ekin imzalı görüntüleme adresi — erişim dosya üzerinden denetlenir. */
export async function dosyaEkiAdresi(projectId: string, attachmentId: string) {
  const { data } = await api.get<{ url: string; filename: string; mimeType: string }>(
    `/elevator-projects/${projectId}/attachments/${attachmentId}/url`,
  )
  return data
}

export async function dosyaMenusuEkle(projectId: string, body: { label: string; auditSetMenuId?: string | null; parentId?: string | null }) {
  const { data } = await api.post<DosyaMenusu>(`/elevator-projects/${projectId}/menus`, body)
  return data
}

export async function dosyaMenusuAdlandir(projectId: string, menuId: string, label: string) {
  await api.patch(`/elevator-projects/${projectId}/menus/${menuId}`, { label })
}

export async function dosyaMenusuSil(projectId: string, menuId: string) {
  await api.delete(`/elevator-projects/${projectId}/menus/${menuId}`)
}

/** Dosyaya özel formun, form örneklerinde kullanılan öğe kimliği. */
export const dosyaFormuOgeKimligi = (ekId: string) => `ek:${ekId}`

export async function dosyaEkiSil(projectId: string, attachmentId: string) {
  await api.delete(`/elevator-projects/${projectId}/attachments/${attachmentId}`)
}

/**
 * FORM GÖREVLENDİRMESİ — denetim setinde forma birim (görev türü) atanır;
 * faaliyet dosyasında formu dolduracak personel seçilir ve o türde görev açılır.
 */
export interface FormAtamasi {
  auditSetItemId: string
  taskId: string
  status: "PENDING" | "ACCEPTED" | "REJECTED" | "COMPLETED"
  /** Personelin kabul/ret yanıtı. */
  respondedAt?: string | null
  rejectionReason?: string | null
  completedAt?: string | null
  taskTypeCode: string
  personnel: { id: string; code: string; name: string }
}

export async function fetchFormAtamalari(projectId: string) {
  const { data } = await api.get<FormAtamasi[]>(`/elevator-projects/${projectId}/form-assignments`)
  return data
}

/** Tamamlanmış form görevine yeniden düzenleme yetkisi verir. */
export async function formGoreviniYenidenAc(projectId: string, auditSetItemId: string, taskTypeCode?: string) {
  const { data } = await api.post<FormAtamasi[]>(`/elevator-projects/${projectId}/form-assignments/reopen`, { auditSetItemId, taskTypeCode })
  return data
}

/** Formun bir biriminin (görev türü) görevini kişiye verir; birim verilmezse formun ilk birimi. */
export async function formuAta(projectId: string, auditSetItemId: string, personnelId: string | null, taskTypeCode?: string) {
  const { data } = await api.put<{
    /** `ayniGorev`: aynı görev türünde aynı kişiye verilen diğer form sayısı. */
    result: { field: string; assigned?: string; issues?: { message: string }[]; cancelled: number; skipped?: string; ayniGorev?: number } | null
    assignments: FormAtamasi[]
  }>(`/elevator-projects/${projectId}/form-assignments`, { auditSetItemId, personnelId, taskTypeCode })
  return data
}

/**
 * DOSYANIN KONTROL LİSTELERİ — denetim setine atanmış listelerden, "Alana
 * Bağla" etiketleri (ör. İlgili Standartlar) dosyadaki seçimle eşleşenler.
 */
export interface EslesenKontrolListesi {
  checklistId: string
  auditSetItemId: string
  title: string
  code: string
  revisionNo: string | null
  eslesme: { alan: string; degerler: string[] }[]
  /** Hiçbir alana bağlanmamış — sette olması yeterli. */
  alansiz?: boolean
  /** Setten değil, bu dosyaya sonradan eklendi (Yükle / Ekle). */
  dosyayaOzel?: boolean
  attachmentId?: string | null
}

export async function fetchEslesenKontrolListeleri(projectId: string) {
  const { data } = await api.get<{
    listeler: EslesenKontrolListesi[]
    /** Sete atanmış toplam liste sayısı. */
    atanmis: number
    /** Listelerin bağlı olduğu alanlar ve dosyada doldurulup doldurulmadığı. */
    alanlar: { key: string; label: string; dolu: boolean }[]
    /** Setteki her kontrol listesi öğesinin güncel revizyon kimliği. */
    guncel?: Record<string, string>
  }>(`/elevator-projects/${projectId}/checklists`)
  return data
}

// === Denetim ekibi (birim → personel, formdan bağımsız) ===

export interface DenetimEkibi {
  /** Dosyanın denetim setine özel birimler; `kod` = "ozel:<id>". */
  ozelBirimler: { id: string; kod: string; ad: string }[]
  uyeler: {
    id: string
    birimKodu: string
    /** Pasif üye dosyayı görür, değiştiremez. */
    aktif: boolean
    /** Dosyada görevi ya da işlem kaydı var: silinemez, pasife alınır. */
    aktiviteVar: boolean
    personnel: { id: string; code: string; name: string }
  }[]
}

export const denetimEkibiAnahtari = (projectId: string) => ["elevator-project-ekip", projectId] as const

export async function fetchDenetimEkibi(projectId: string) {
  const { data } = await api.get<DenetimEkibi>(`/elevator-projects/${projectId}/ekip`)
  return data
}

export async function ekipUyesiEkle(projectId: string, birimKodu: string, personnelId: string) {
  const { data } = await api.post<DenetimEkibi>(`/elevator-projects/${projectId}/ekip/uyeler`, { birimKodu, personnelId })
  return data
}

export async function ekipUyesiDurumu(projectId: string, uyeId: string, aktif: boolean) {
  const { data } = await api.patch<DenetimEkibi>(`/elevator-projects/${projectId}/ekip/uyeler/${uyeId}`, { aktif })
  return data
}

export async function ekipUyesiCikar(projectId: string, uyeId: string) {
  const { data } = await api.delete<DenetimEkibi>(`/elevator-projects/${projectId}/ekip/uyeler/${uyeId}`)
  return data
}

export async function ozelBirimEkle(projectId: string, ad: string) {
  const { data } = await api.post<DenetimEkibi>(`/elevator-projects/${projectId}/ekip/birimler`, { ad })
  return data
}

export async function ozelBirimSil(projectId: string, birimId: string) {
  const { data } = await api.delete<DenetimEkibi>(`/elevator-projects/${projectId}/ekip/birimler/${birimId}`)
  return data
}

// === PDF/Yazdır'a gönderilen belgeler ===

/** `harici`: formun harici dokümanı; `ek`: denetim setine dosyaya özel yüklenen belge. */
export type CiktiKaynagi = "harici" | "ek"

export interface CiktiDokumani {
  kaynak: CiktiKaynagi
  id: string
  /** Geldiği yer: formun adı ya da yüklemenin başlığı. */
  kaynakAdi: string | null
  file: { id: string; filename: string; mimeType: string; size: number }
}

export const ciktiDokumanlariAnahtari = (projectId: string) => ["elevator-project", projectId, "cikti-dokumanlari"] as const

export async function fetchCiktiDokumanlari(projectId: string) {
  const { data } = await api.get<CiktiDokumani[]>(`/elevator-projects/${projectId}/cikti-dokumanlari`)
  return data
}

export async function ciktiyaGonder(projectId: string, kaynak: CiktiKaynagi, id: string, ciktida: boolean) {
  const { data } = await api.patch<{ ok: true; ciktida: boolean }>(`/elevator-projects/${projectId}/cikti-dokumanlari/${kaynak}/${id}`, { ciktida })
  return data
}
